import { Box, Checkbox, IconButton } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AddIcon from "@mui/icons-material/Add";
import MinusIcon from "@mui/icons-material/Remove";
import React, { useEffect, useState, useRef } from "react";
import theme from "Theme/AppTheme";
import { SourceFieldType } from "../types";
import DeleteButton from "../components/DeleteButton";
import useHoverAction from "../hooks/useHoverAction";
import useDebouncedUpdate from "../hooks/useDebouncedUpdate";
import {
    EXTRACTION_PATTERN_HELP,
    getExtractionPatternWarning,
} from "../helpers/extractionPattern";

type SourceFieldProps = SourceFieldType & {
    onRemove: () => void;
    onChange: (field: SourceFieldType) => void;
    hasMissingError: boolean;
    hasDuplicateError: boolean;
};

const SourceField: React.FC<SourceFieldProps> = ({
    onRemove,
    onChange,
    path,
    alias,
    extractionPattern,
    isRequired,
    id,
    targetFieldName,
    hasMissingError,
    hasDuplicateError,
}) => {
    const [showPattern, setShowPattern] = useState(Boolean(extractionPattern)); // show extraction pattern input if extraction pattern exists
    const containerRef = useRef<HTMLLIElement>(null);
    const { showAction, handlers } = useHoverAction(containerRef);

    const [_path, setPath] = useState(path);
    const [_alias, setAlias] = useState(alias);
    const [_extractionPattern, setExtractionPattern] = useState(
        extractionPattern ?? "",
    );

    // The local copy above is only set on mount, so when React reuses this component for a different
    // row it keeps showing the old row's pattern. Remember what we sent up. anything else that
    // arrives came from outside, so take it.
    const lastPushed = useRef({ path, alias, extractionPattern });

    useEffect(() => {
        const sent = lastPushed.current;

        if (path !== sent.path) setPath(path);
        if (alias !== sent.alias) setAlias(alias);
        if ((extractionPattern ?? "") !== (sent.extractionPattern ?? "")) {
            setExtractionPattern(extractionPattern ?? "");
            setShowPattern(Boolean(extractionPattern));
        }

        lastPushed.current = { path, alias, extractionPattern };
    }, [path, alias, extractionPattern]);

    // Say so when a pattern cannot extract anything. Does not block the save
    const patternWarning = getExtractionPatternWarning(_extractionPattern);

    useDebouncedUpdate(() => handleChange(_path, "path"), [_path]);
    useDebouncedUpdate(() => handleChange(_alias, "alias"), [_alias]);
    useDebouncedUpdate(() => {
        handleChange(_extractionPattern.trim() === "" ? null : _extractionPattern, "extractionPattern");
    }, [_extractionPattern]);

    const handleChange = (
        value: string | boolean | null,
        field: string,
    ) => {
        // Use what is in the inputs right now
        const updated: SourceFieldType = {
            path: _path,
            alias: _alias,
            extractionPattern:
                _extractionPattern.trim() === "" ? null : _extractionPattern,
            isRequired,
            id,
            targetFieldName,
            [field]: value,
        };

        lastPushed.current = {
            path: updated.path,
            alias: updated.alias,
            extractionPattern: updated.extractionPattern,
        };

        onChange(updated);
    };

    return (
        <Box
            ref={containerRef}
            {...handlers}
            sx={{
                borderRadius: 2,
                padding: theme.spacing(1, 0.5),
                background: theme.palette.white.main,
                display: "flex",
            }}
            component="li"
        >
            <Checkbox
                size="small"
                checked={isRequired}
                onChange={(e) => handleChange(e.target.checked, "isRequired")}
                sx={{
                    padding: theme.spacing(0.5),
                    margin: theme.spacing(0.5, 1.5, 0, 0),
                    height: "fit-content",
                    "svg.MuiSvgIcon-root": {
                        width: "20px",
                        height: "20px",
                    },
                }}
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    gap: theme.spacing(1),
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <AcxMainTextField
                        id={`source-field-path-${id}`}
                        type="text"
                        placeholderText="Field Path"
                        value={path}
                        onChange={(e) => setPath(e.target.value)}
                        containerStyle={{
                            flex: 5,
                            marginRight: theme.spacing(3),
                        }}
                        error={hasMissingError && !path}
                        hideErrorIcon
                        fullWidth
                    />
                    <AcxMainTextField
                        id={`source-field-alias-${id}`}
                        type="text"
                        placeholderText="Source Field Alias"
                        value={alias}
                        onChange={(e) => setAlias(e.target.value)}
                        containerStyle={{
                            flex: 2,
                            marginRight: theme.spacing(0.5),
                        }}
                        error={(hasMissingError && !alias) || hasDuplicateError}
                        hideErrorIcon
                        fullWidth
                    />
                    <IconButton
                        size="small"
                        onClick={() => {
                            setShowPattern(true);
                        }}
                        sx={{
                            padding: theme.spacing(0.5),
                            height: "fit-content",
                            visibility: showPattern ? "hidden" : "visible",
                        }}
                    >
                        <AddIcon fontSize="inherit" />
                    </IconButton>
                </Box>
                {showPattern && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <AcxMainTextField
                            id={`source-field-pattern-${id}`}
                            type="text"
                            placeholderText="Enter Regex extraction pattern"
                            value={_extractionPattern}
                            onChange={(e) =>
                                setExtractionPattern(e.target.value)
                            }
                            containerStyle={{
                                flex: 2,
                                marginRight: theme.spacing(0.5),
                            }}
                            error={Boolean(patternWarning)}
                            hideErrorIcon
                            helperText={patternWarning ?? EXTRACTION_PATTERN_HELP}
                            fullWidth
                        />
                        <IconButton
                            size="small"
                            onClick={() => {
                                setExtractionPattern("");
                                handleChange(null, "extractionPattern");
                                setShowPattern(false);
                            }}
                            sx={{
                                padding: theme.spacing(0.5),
                                height: "fit-content",
                            }}
                        >
                            <MinusIcon fontSize="inherit" />
                        </IconButton>
                    </Box>
                )}
            </Box>
            <DeleteButton
                onDelete={onRemove}
                showDelete={showAction}
                sx={{ margin: theme.spacing(1, 0.5) }}
            />
        </Box>
    );
};

export default SourceField;
